import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import { stockAdjustmentApi } from '../api/stockAdjustment';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';

const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

const StockAdjustment: React.FC = () => {
  const { showToast } = useToast();
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [total, setTotal]     = useState(0);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [branches, setBranches]   = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [stockItems, setStockItems] = useState<any[]>([]);
  const [form, setForm] = useState({ branch_id:'', location_id:'', adjustment_date: new Date().toISOString().split('T')[0], reason:'' });
  const [lines, setLines] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    stockAdjustmentApi.list({ search }).then(res => { setAdjustments(res.data.data); setTotal(res.data.total); setLoading(false); });
  }, [search]);

  useEffect(() => {
    purchaseApi.branches().then(r => setBranches(r.data.data));
    purchaseApi.locations().then(r => setLocations(r.data.data));
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const loadStock = async (branchId: string, locationId: string) => {
    if (!branchId) return;
    const res = await stockAdjustmentApi.getStock({ branch_id: branchId, location_id: locationId || undefined });
    setStockItems(res.data.data);
    setLines(res.data.data.map((item: any) => ({
      product_id:   item.product_id,
      location_id:  item.location_id,
      product_name: item.product_name,
      sku:          item.sku,
      unit:         item.unit,
      location_name:item.location_name,
      branch_name:  item.branch_name,
      system_qty:   Number(item.qty_on_hand),
      physical_qty: Number(item.qty_on_hand),
      unit_cost:    Number(item.unit_cost) || Number(item.cost_price),
      difference:   0,
    })));
  };

  const updateLine = (i: number, val: string) => {
    setLines(l => l.map((line, idx) => idx===i ? {...line, physical_qty: Number(val), difference: Number(val) - line.system_qty} : line));
  };

  const changedLines = lines.filter(l => l.difference !== 0);

  const handleSubmit = async () => {
    if (!changedLines.length) { showToast('No changes detected', 'error'); return; }
    if (!form.branch_id) { showToast('Branch required', 'error'); return; }
    setSaving(true);
    try {
      const res = await stockAdjustmentApi.create({
        branch_id:       Number(form.branch_id),
        adjustment_date: form.adjustment_date,
        reason:          form.reason,
        items:           lines.map(l => ({ product_id:l.product_id, location_id:l.location_id, system_qty:l.system_qty, physical_qty:l.physical_qty, unit_cost:l.unit_cost })),
      });
      showToast(res.data.message);
      setShowForm(false); setLines([]); setStockItems([]);
      setForm({ branch_id:'', location_id:'', adjustment_date: new Date().toISOString().split('T')[0], reason:'' });
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Stock Adjustment</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13}/> New Adjustment</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Physical Count Adjustment</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => { setForm(f=>({...f,branch_id:e.target.value})); loadStock(e.target.value, form.location_id); }}>
                  <option value="">Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Location (optional)</label>
                <select className="form-control" value={form.location_id} onChange={e => { setForm(f=>({...f,location_id:e.target.value})); loadStock(form.branch_id, e.target.value); }}>
                  <option value="">All Locations</option>
                  {locations.filter(l => !form.branch_id || String(l.branch_id) === form.branch_id).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Date *</label>
                <input type="date" className="form-control" value={form.adjustment_date} onChange={e => setForm(f=>({...f,adjustment_date:e.target.value}))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom:14 }}>
              <label className="form-label">Reason</label>
              <input className="form-control" value={form.reason} onChange={e => setForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Annual physical count" />
            </div>

            {lines.length > 0 && (
              <>
                <div style={{ marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--tx2)' }}>
                    {lines.length} items — <span style={{ color: changedLines.length > 0 ? 'var(--amber)' : 'var(--tx3)' }}>{changedLines.length} changed</span>
                  </span>
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || changedLines.length === 0}>
                    {saving ? 'Posting...' : `Post Adjustment (${changedLines.length} changes)`}
                  </button>
                </div>
                <div style={{ maxHeight:400, overflowY:'auto', border:'1px solid var(--bdr)', borderRadius:7 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'var(--sur2)', position:'sticky', top:0 }}>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>System Qty</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Physical Qty</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Difference</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Impact</th>
                    </tr></thead>
                    <tbody>
                      {lines.map((line, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid var(--bdr)', background: line.difference !== 0 ? 'rgba(255,200,0,0.05)' : undefined }}>
                          <td style={{ padding:'6px 12px', fontFamily:'monospace', fontSize:12, color:'var(--blue)' }}>{line.sku}</td>
                          <td style={{ padding:'6px 12px', fontSize:12 }}>{line.product_name}</td>
                          <td style={{ padding:'6px 12px', fontSize:11, color:'var(--tx3)' }}>{line.location_name}</td>
                          <td style={{ padding:'6px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12, color:'var(--tx3)' }}>{line.system_qty}</td>
                          <td style={{ padding:'6px 4px', width:100 }}>
                            <input type="number" className="form-control mono" value={line.physical_qty} min={0}
                              onChange={e => updateLine(i, e.target.value)}
                              style={{ textAlign:'right', background: line.difference !== 0 ? 'rgba(255,200,0,0.1)' : undefined }} />
                          </td>
                          <td style={{ padding:'6px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12, fontWeight:700,
                            color: line.difference > 0 ? 'var(--green)' : line.difference < 0 ? 'var(--red)' : 'var(--tx3)' }}>
                            {line.difference > 0 ? '+' : ''}{line.difference}
                          </td>
                          <td style={{ padding:'6px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{Number(line.unit_cost).toFixed(2)}</td>
                          <td style={{ padding:'6px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12, fontWeight:600,
                            color: line.difference > 0 ? 'var(--green)' : line.difference < 0 ? 'var(--red)' : 'var(--tx3)' }}>
                            {line.difference !== 0 ? (line.difference > 0 ? '+' : '') + (line.difference * line.unit_cost).toFixed(2) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {lines.length === 0 && form.branch_id && (
              <div className="empty-state"><p>No stock found for selected branch/location.</p></div>
            )}
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)"/><input placeholder="Search adjustments..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} records</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Reference</th><th>Branch</th><th>Date</th><th>Reason</th><th style={{textAlign:'right'}}>Increase</th><th style={{textAlign:'right'}}>Decrease</th><th>Status</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            : adjustments.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><p>No stock adjustments yet.</p></div></td></tr>
            : adjustments.map(a => (
              <tr key={a.id}>
                <td className="td-code">{a.reference_no}</td>
                <td style={{color:'var(--tx3)'}}>{a.branch_name}</td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(a.adjustment_date).toLocaleDateString()}</td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{a.reason||'—'}</td>
                <td className="td-num td-green">{Number(a.total_increase).toFixed(2)}</td>
                <td className="td-num td-red">{Number(a.total_decrease).toFixed(2)}</td>
                <td><span className={`badge ${colorMap[a.status_color]||'badge-gray'}`}>{a.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default StockAdjustment;
